import { useEffect, useMemo, useRef } from "react";
import {
  useGetJewelryVariation,
  useGetJewelryComponents,
  useAddToJewelryOptions,
} from "@apis/diamondDetail";
import { ProductCategory } from "@utils/constants";
import { isJewelry } from "@utils/product/productValidators";
import { Diamond } from "@src/types/DiamondDetail";

interface UsePdpJewelryDataProps {
  route: any;
  isFocused: boolean;
  isModal: boolean;
  product_type: ProductCategory;
  priceMode: number;
  userInfo: any;
  isVariationRequired: boolean;
  shouldShowJewelryComponents: (itemDetail: any) => boolean;
  enableJewelryBuilder: boolean;
  enterSecondFlow: boolean;
  initialItemDetail: any;
  itemDetail: Diamond;
  getDiamondDetailData: any;
  additionalAttributes?: any;
  setUserSelectedSlug: (slug: any) => void;
  setCustomDesignConfiguration: (config: boolean) => void;
  updateJcSelectedOptions: (item: any, enterSecondFlow?: boolean) => void;
  appendNewVariation: (variations: any[]) => void;
  setAllTypes: (item: any) => void;
  setMatchingPairItemDetail: (item: any) => void;
}

export const usePdpJewelryData = ({
  route,
  isFocused,
  isModal,
  product_type,
  priceMode,
  userInfo,
  isVariationRequired,
  shouldShowJewelryComponents,
  enableJewelryBuilder,
  enterSecondFlow,
  initialItemDetail,
  itemDetail,
  getDiamondDetailData,
  additionalAttributes,
  setUserSelectedSlug,
  setCustomDesignConfiguration,
  updateJcSelectedOptions,
  appendNewVariation,
  setAllTypes,
  setMatchingPairItemDetail,
}: UsePdpJewelryDataProps) => {
  // Refs for jewelry-specific processing
  const processedConfiguratorRef = useRef<string>("");
  const processedVariationRef = useRef<string>("");

  // Jewelry variation data
  const {
    data: jewelryVariationData,
    isSuccess: variationSuccess,
    isFetching: variationIsFetching,
    isFetched: variationIsFetched,
  } = useGetJewelryVariation(
    initialItemDetail?.id as number,
    priceMode,
    userInfo.getFeatureSettings().variations_price_attachment ? 10 : 1,
    isVariationRequired &&
      !isModal &&
      isJewelry(product_type) &&
      itemDetail?.type === product_type &&
      initialItemDetail?.id,
    undefined,
    undefined,
    userInfo.user?.locale
  );

  // Jewelry components data
  const {
    data: componentsData,
    isLoading: componentsLoading,
    isFetching: componentsFetching,
  } = useGetJewelryComponents(
    route?.params?.productID as number,
    priceMode,
    isJewelry(product_type) &&
      shouldShowJewelryComponents(initialItemDetail) &&
      !isModal
  );

  // Jewelry options data
  const getAddToJewelryOptionsData = useAddToJewelryOptions(
    [route?.params?.productID as number],
    !isModal,
    enableJewelryBuilder &&
      enterSecondFlow === false &&
      [route?.params?.productID as number].length > 0
  );

  // Configure jewelry configurator
  const configureJewelryConfigurator = () => {
    setUserSelectedSlug(itemDetail.slug_detail ?? {});
    setCustomDesignConfiguration(
      itemDetail?.custom_design_configuration ?? false
    );
  };

  const stableSlugDetail = useMemo(
    () => itemDetail?.slug_detail,
    [JSON.stringify(itemDetail?.slug_detail)]
  );

  const stableVariationData = useMemo(
    () => jewelryVariationData?.data?.response?.body?.variations,
    [jewelryVariationData?.data]
  );

  // Reset jewelry-specific processed refs when variation config changes
  useEffect(() => {
    processedConfiguratorRef.current = "";
    processedVariationRef.current = "";
  }, [additionalAttributes?.config]);

  useEffect(() => {
    if (isFocused && itemDetail?.id) {
      processedConfiguratorRef.current = "";
      processedVariationRef.current = "";
    }
  }, [isFocused, itemDetail?.id, priceMode, userInfo.user?.locale]);

  // Jewelry configurator setup effect. Only the focused setting page may
  // write the shared metal selection. A setting left on the stack still
  // resolves its original fetch after Find a Match clears the live item,
  // and applying that here puts the default metal back.
  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (
      !isJewelry(product_type) ||
      !itemDetail?.display_configuration_v2 ||
      !itemDetail?.id
    ) {
      return;
    }

    // Create unique key to prevent re-processing
    const configuratorKey = `${itemDetail.id}-${JSON.stringify(
      stableSlugDetail || {}
    )}`;

    // Skip if already processed
    if (processedConfiguratorRef.current === configuratorKey) {
      return;
    }

    // Mark as processed BEFORE calling functions to prevent re-triggering
    processedConfiguratorRef.current = configuratorKey;

    configureJewelryConfigurator();
    updateJcSelectedOptions(itemDetail, enterSecondFlow);
  }, [
    isFocused,
    itemDetail?.id,
    itemDetail?.display_configuration_v2,
    product_type,
    stableSlugDetail,
  ]);

  // Variation data processing effect
  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (
      !variationSuccess ||
      !variationIsFetched ||
      variationIsFetching ||
      !getDiamondDetailData?.data ||
      !itemDetail?.id ||
      !stableVariationData?.[0]
    ) {
      return;
    }

    // Create unique key to prevent re-processing
    const firstVariation = stableVariationData?.[0];
    const variationItem = firstVariation?.item;
    const variationKey = `${itemDetail?.id}-${variationItem?.id}-${priceMode}-${
      userInfo.user?.locale
    }-${JSON.stringify(firstVariation || {})}`;

    // Skip if already processed
    if (processedVariationRef.current === variationKey) {
      return;
    }

    // Mark as processed BEFORE calling functions to prevent re-triggering
    processedVariationRef.current = variationKey;

    appendNewVariation(stableVariationData);
    if (variationItem?.display_configuration_v2) {
      setAllTypes(variationItem);
    } else if (userInfo.getFeatureSettings().variations_price_attachment) {
      setAllTypes(itemDetail);
    }
  }, [
    isFocused,
    variationSuccess,
    variationIsFetched,
    variationIsFetching,
    stableVariationData,
    getDiamondDetailData?.data,
    itemDetail?.id,
    priceMode,
    userInfo.user?.locale,
  ]);

  // Reset matching pair item detail effect
  useEffect(() => {
    if (
      enterSecondFlow === false &&
      enableJewelryBuilder &&
      isFocused &&
      !isModal
    ) {
      setMatchingPairItemDetail((prev) => {
        // ✅ prevent re-render if already empty
        if (prev && Object.keys(prev).length === 0) {
          return prev;
        }
        return {};
      });
    }
  }, [enterSecondFlow, enableJewelryBuilder, isFocused, isModal]);

  return {
    jewelryVariationData,
    variationSuccess,
    variationIsFetching,
    variationIsFetched,
    componentsData,
    componentsLoading,
    componentsFetching,
    getAddToJewelryOptionsData,
  };
};
